import AutoCompleteWithPaginate from '@/components/AutoCompletePaginate';
import AutoCompleteWithTag from '@/components/AutoCompleteWithTag';
import BoldText from '@/components/BoldText';
import { CriteriaType } from '@/constants/enum';
import useCountry from '@/hooks/useCountry';
import UserPlans from '@/hooks/userPlans';
import useErrorRule from '@/pages/block-list/hooks/useErrorRule';
import { apiCaller } from '@/redux/query';
import blockListSlice, { inputCollectionSelector, inputProductSelector, settingSelector } from '@/redux/slice/blockList.slice';
import { IOptions } from '@/types/components';
import _debounce from 'lodash/debounce';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const Product = () => {
  const { t } = useTranslation(['common']);
  const useError = useErrorRule();
  const dispatch = useDispatch();
  const [fetchProduct, fetchListProductStatus] = apiCaller.useLazyProductQuery();
  const [fetchListCollection, fetchListCollectionStatus] = apiCaller.useLazyListCollectionQuery();
  const [listProduct, setListProduct] = useState<Array<{ label: string; value: string }>>([]);
  const [listCollections, setListCollections] = useState<Array<{ label: string; value: string }>>([]);
  const inputProduct = useSelector(inputProductSelector);
  const inputCollection = useSelector(inputCollectionSelector);
  const blockRuleSelected = useSelector(settingSelector);
  const { userPlanFree } = UserPlans();
  const handleCountry = useCountry({
    countryCode: blockRuleSelected.country ? blockRuleSelected.country[0] : undefined,
    stateName: blockRuleSelected.state ? blockRuleSelected.state[0] : undefined,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleSearchProduct = useCallback(
    _debounce((value: string) => {
      dispatch(blockListSlice.actions.handleInputProduct(value));
    }, 500),
    [],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleSearchCollection = useCallback(
    _debounce((value: string) => {
      dispatch(blockListSlice.actions.handleInputCollection(value));
    }, 500),
    [],
  );
  useEffect(() => {
    if (blockRuleSelected.criteria === CriteriaType.Collection) {
      const fetchData = async () => {
        fetchListCollection({ search: inputCollection }).then((res) => {
          if (res.data) {
            const list = res.data.listCollections.map((item) => {
              return {
                label: item.title,
                value: item.id.toString(),
              };
            });
            setListCollections(list);
          }
        });
      };
      fetchData();
    } else {
      const fetchData = () => {
        fetchProduct({ search: inputProduct }).then((res) => {
          if (res.data) {
            const list = res.data.listProducts.map(({ title, id }) => {
              return {
                label: title,
                value: id.toString(),
              };
            });
            setListProduct(list);
          }
        });
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockRuleSelected.criteria, inputProduct, inputCollection]);
  return (
    <div>
      {blockRuleSelected.criteria === CriteriaType.Collection ? (
        <AutoCompleteWithPaginate
          tag={
            blockRuleSelected.collectionName && blockRuleSelected.collectionId
              ? blockRuleSelected.collectionId.map((item, index) => {
                  return {
                    value: item,
                    label: blockRuleSelected.collectionName ? blockRuleSelected.collectionName[index] : '',
                  };
                })
              : []
          }
          handleSearch={debounceHandleSearchCollection}
          helpText={!blockRuleSelected.id ? t('common:select_multiple_collections') : undefined}
          placeholder={`-${t('common:collection')}-`}
          disable={fetchListCollectionStatus.isLoading || userPlanFree}
          options={listCollections}
          label={t('common:select_collection')}
          selectedOptions={blockRuleSelected.collectionId || []}
          setSelectedOptions={(selected: IOptions[]) => {
            if (selected.length === 0) {
              useError.handleErrorRule(t('common:collection_must_be_added'));
            } else if (useError.errorSelected?.error) {
              useError.removeErrorRule();
            }
            dispatch(
              blockListSlice.actions.handleSetting({
                ...blockRuleSelected,
                collectionId: selected.map((item) => item.value),
                collectionName: selected.map((item) => item.label),
              }),
            );
          }}
          allowMultiple={!blockRuleSelected.id}
          requiredIndicator
          error={useError.errorSelected?.error}
          onBlur={() => {
            if (blockRuleSelected.collectionId?.length === 0) {
              useError.handleErrorRule(t('common:collection_must_be_added'));
            }
          }}
        />
      ) : null}

      {blockRuleSelected.criteria === CriteriaType.Product ? (
        <AutoCompleteWithPaginate
          tag={
            blockRuleSelected.productName && blockRuleSelected.productId
              ? blockRuleSelected.productId.map((item, index) => {
                  return {
                    value: item,
                    label: blockRuleSelected.productName ? blockRuleSelected.productName[index] : '',
                  };
                })
              : []
          }
          handleSearch={debounceHandleSearchProduct}
          helpText={!blockRuleSelected.id ? t('common:select_multiple_products') : undefined}
          placeholder={t('common:search')}
          disable={fetchListProductStatus.isLoading || userPlanFree}
          options={listProduct}
          label={t('common:select_product')}
          selectedOptions={blockRuleSelected.productId || []}
          setSelectedOptions={(selected: IOptions[]) => {
            if (selected.length === 0) {
              useError.handleErrorRule(t('common:product_must_be_added'));
            } else if (useError.errorSelected?.error) {
              useError.removeErrorRule();
            }
            dispatch(
              blockListSlice.actions.handleSetting({
                ...blockRuleSelected,
                productId: selected.map((item) => item.value),
                productName: selected.map((item) => item.label),
              }),
            );
          }}
          allowMultiple={!blockRuleSelected.id}
          requiredIndicator
          error={useError.errorSelected?.error}
          onBlur={() => {
            if (blockRuleSelected.productId?.length === 0) {
              useError.handleErrorRule(t('common:product_must_be_added'));
            }
          }}
        />
      ) : null}

      <BoldText>{t('common:from')}</BoldText>

      <AutoCompleteWithTag
        options={[...handleCountry.otherCountry, ...handleCountry.listCountry]}
        label={t('common:select_country')}
        selectedOptions={blockRuleSelected.country || []}
        placeholder={`-${t('common:country')}-`}
        setSelectedOptions={(value) => {
          if (value.length === 0) {
            useError.handleErrorRule(t('common:country_must_be_added'), CriteriaType.Country);
          } else if (useError.getErrorRuleByCriteria(CriteriaType.Country)?.error) {
            useError.removeErrorRule(CriteriaType.Country);
          }
          if (useError.errorSelected?.error) {
            useError.removeErrorRule();
          }
          dispatch(
            blockListSlice.actions.handleSetting({
              ...blockRuleSelected,
              country: value,
            }),
          );
        }}
        allowMultiple={false}
        requiredIndicator
        error={useError.getErrorRuleByCriteria(CriteriaType.Country)?.error}
        onBlur={() => {
          if (blockRuleSelected.country?.length === 0) {
            useError.handleErrorRule(t('common:country_must_be_added'), CriteriaType.Country);
          }
        }}
        disable={userPlanFree}
      />
    </div>
  );
};

export default memo(Product);
